import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal, Kicker } from "./Reveal";
import { OFFERS } from "@/data/offers";
import { trackClick } from "@/lib/analytics";
import { trackLinkedInConversion, LINKEDIN_CONVERSIONS } from "@/lib/linkedinConversions";
import { downloadWhitepaper, CONSENT_TEXT } from "@/lib/whitepaper";
import { Send, FileText, CheckCircle2, Download } from "lucide-react";

export const ConsentNotice = () => (
  <p className="text-[11px] leading-relaxed text-slate-500 italic" data-testid="consent-notice">
    En renseignant ce formulaire, vous acceptez que vos données soient utilisées pour traiter votre demande.
    ASAP Technologies pourra également vous contacter au sujet de contenus et offres professionnels en lien
    avec votre activité. Vous pouvez vous y opposer à tout moment. En savoir plus dans notre{" "}
    <Link to="/confidentialite" className="text-teal-400 hover:text-teal-300 not-italic underline underline-offset-2" data-testid="consent-privacy-link">
      Politique de confidentialité
    </Link>.
  </p>
);

export const PrivacyMention = ({ withPdf = false }) => (
  <p className="text-[11px] leading-relaxed text-slate-500 mt-3" data-testid="privacy-mention">
    {withPdf ? "Accès immédiat au PDF. " : ""}Vos coordonnées restent confidentielles et ne sont pas transmises à des tiers.
  </p>
);

const EMPTY = { nom: "", prenom: "", entreprise: "", fonction: "", email: "", message: "", offre: "", website: "" };

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs text-slate-400 mb-1.5 block">
      {label} {required && <span className="text-teal-400">*</span>}
    </span>
    {children}
  </label>
);

const LeadForm = ({ type }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isWp = type === "whitepaper";

  // Conversion LinkedIn : déclenchée une seule fois à l'entrée dans l'état de
  // confirmation (pas au clic, pas lors d'un "Télécharger à nouveau").
  const convFired = useRef(false);
  useEffect(() => {
    if (!done) {
      convFired.current = false;
      return;
    }
    if (isWp && !convFired.current) {
      convFired.current = true;
      trackLinkedInConversion(LINKEDIN_CONVERSIONS.whitepaper);
    }
  }, [done, isWp]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...form, lead_type: type });
      setDone(true);
      setForm(EMPTY);
      trackClick(isWp ? "lead_whitepaper" : "lead_contact");
      if (isWp) {
        downloadWhitepaper();
        toast.success("Merci ! Le téléchargement de votre livre blanc démarre.");
      } else {
        toast.success("Votre demande a bien été envoyée. Nous revenons vers vous rapidement.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="flex flex-col items-center justify-center text-center py-14" data-testid={`lead-success-${type}`}>
        <CheckCircle2 size={40} className="text-teal-400" />
        <p className="mt-4 font-heading text-lg font-semibold text-slate-100">Merci !</p>
        <p className="mt-1 text-sm text-slate-400 max-w-xs">
          {isWp ? "Votre livre blanc a été envoyé par email. Le téléchargement a aussi démarré." : "Votre demande a bien été transmise à nos équipes."}
        </p>
        {isWp && (
          <button onClick={downloadWhitepaper} className="btn-primary mt-5" data-testid="whitepaper-download-again">
            <Download size={15} /> Télécharger le livre blanc
          </button>
        )}
        <button onClick={() => setDone(false)} className="mt-4 text-xs text-teal-300 hover:text-teal-200" data-testid={`lead-again-${type}`}>
          {isWp ? "Renseigner un autre e-mail" : "Envoyer une autre demande"}
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`lead-form-${type}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nom" required>
          <input data-testid={`${type}-input-nom`} className="input-dark" required value={form.nom} onChange={set("nom")} placeholder="Dupont" />
        </Field>
        <Field label="Prénom" required>
          <input data-testid={`${type}-input-prenom`} className="input-dark" required value={form.prenom} onChange={set("prenom")} placeholder="Marie" />
        </Field>
        <Field label="Entreprise" required>
          <input data-testid={`${type}-input-entreprise`} className="input-dark" required value={form.entreprise} onChange={set("entreprise")} placeholder="Votre société" />
        </Field>
        <Field label="Fonction" required>
          <input data-testid={`${type}-input-fonction`} className="input-dark" required value={form.fonction} onChange={set("fonction")} placeholder="DSI, Directeur Produit…" />
        </Field>
        <Field label="E-mail" required>
          <input data-testid={`${type}-input-email`} className="input-dark" type="email" required value={form.email} onChange={set("email")} placeholder="vous@entreprise.fr" />
        </Field>
      </div>
      {!isWp && (
        <>
          <Field label="Offre concernée">
            <select data-testid="contact-select-offre" className="input-dark" value={form.offre} onChange={set("offre")}>
              <option value="">— Sélectionner une offre (optionnel) —</option>
              {OFFERS.map((o) => (
                <option key={o.id} value={o.title}>{o.title}</option>
              ))}
              <option value="Autre">Autre demande</option>
            </select>
          </Field>
          <Field label="Votre besoin">
            <textarea data-testid="contact-input-message" className="input-dark min-h-[100px]" value={form.message} onChange={set("message")} placeholder="Décrivez votre contexte en quelques lignes…" />
          </Field>
        </>
      )}
      <input name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <button data-testid={`${type}-submit-btn`} disabled={busy} className="btn-primary w-full sm:w-auto disabled:opacity-60">
        {busy ? "Envoi en cours…" : isWp ? "Télécharger le livre blanc" : "Envoyer ma demande"}{" "}
        {isWp ? <Download size={15} /> : <Send size={15} />}
      </button>
      <PrivacyMention withPdf={isWp} />
      {isWp && <ConsentNotice />}
    </form>
  );
};

export default function LeadForms() {
  const [tab, setTab] = useState("contact");
  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 bg-[#0d1322] noise" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Kicker>Contact</Kicker>
            <h2 className="mt-5 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50">
              Parlons de votre <span className="grad-text">contexte.</span>
            </h2>
            <p className="mt-5 text-slate-400 text-sm sm:text-base leading-relaxed max-w-md">
              Un diagnostic à cadrer, une équipe à former, un référentiel à structurer ?
              Laissez-nous vos coordonnées : un expert du partenariat vous recontacte sous 48h.
            </p>
            <div className="mt-8 space-y-3">
              {["Réponse sous 48h ouvrées", "Premier échange sans engagement", "Vos données restent confidentielles"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm text-slate-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal-400" /> {t}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-[#111827] p-7 sm:p-9">
              <div className="flex gap-2 mb-8">
                <button
                  data-testid="tab-contact"
                  onClick={() => setTab("contact")}
                  className={`px-4 py-2 text-xs font-mono2 uppercase tracking-[0.15em] border transition-colors ${
                    tab === "contact" ? "border-teal-400/60 text-teal-300 bg-teal-400/5" : "border-white/10 text-slate-500 hover:text-slate-300"
                  }`}
                >
                  Prise de contact
                </button>
                <button
                  data-testid="tab-whitepaper"
                  onClick={() => setTab("whitepaper")}
                  className={`px-4 py-2 text-xs font-mono2 uppercase tracking-[0.15em] border transition-colors inline-flex items-center gap-2 ${
                    tab === "whitepaper" ? "border-amber-400/60 text-amber-300 bg-amber-400/5" : "border-white/10 text-slate-500 hover:text-slate-300"
                  }`}
                >
                  <FileText size={13} /> Livre blanc
                </button>
              </div>
              {tab === "whitepaper" && (
                <p className="mb-6 text-xs text-amber-300/80 border border-amber-400/20 bg-amber-400/5 px-4 py-3">
                  « Agile… mais toujours lent ? » — les 5 blocages invisibles qui freinent la performance.
                  Renseignez le formulaire pour télécharger le livre blanc.{" "}
                  <Link to="/livre-blanc" className="underline underline-offset-2 hover:text-amber-200" data-testid="whitepaper-landing-link">
                    Découvrir le contenu
                  </Link>
                </p>
              )}
              <LeadForm type={tab} key={tab} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
